import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { MarkdownContent } from '@backstage/core-components';

export interface MarkdownMessageProps {
  content: string;
  isStreaming?: boolean;
}

const COPY_BUTTON_ATTR = 'data-copy-btn';
const COPIED_TIMEOUT_MS = 2000;

/**
 * Matches file paths like `src/foo/bar.ts`, `./utils/index.js:42`,
 * `/absolute/path.tsx:10-20`, or `path/to/file.py`.
 * Group 1: optional preceding whitespace/delimiter (not part of the path).
 * Group 2: optional leading `./`.
 * Group 3: the file path with extension and optional :line or :line-line.
 */
const FILE_PATH_RE =
  /(^|[\s(`"'])(\.\/)?(([\w.@-]+\/)+[\w.-]+\.(?:ts|tsx|js|jsx|py|go|rs|java|rb|css|scss|html|json|yaml|yml|md|sql|sh|env|toml|xml|graphql|proto|vue|svelte)(?::\d+(?:-\d+)?)?)(?=$|[\s)"',;.])/g;

const useStyles = makeStyles(theme => ({
  root: {
    '& p': {
      margin: '0.75em 0',
      fontSize: '0.875rem',
      lineHeight: 1.7,
    },
    '& p:first-child': {
      marginTop: 0,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '1.25em 0 0.5em 0',
      lineHeight: 1.3,
      fontWeight: 600,
    },
    '& h1': { fontSize: '1.25rem' },
    '& h2': { fontSize: '1.1rem' },
    '& h3': { fontSize: '1rem' },
    '& h4, & h5, & h6': { fontSize: '0.875rem' },
    '& ul, & ol': {
      margin: '0.75em 0',
      paddingLeft: theme.spacing(3),
      fontSize: '0.875rem',
    },
    '& li': {
      marginBottom: '0.5em',
      lineHeight: 1.7,
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: '0.8125rem',
      fontFamily: '"Roboto Mono", "Courier New", monospace',
    },
    '& pre': {
      position: 'relative' as const,
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2),
      borderRadius: 8,
      overflow: 'auto',
      margin: '0.75em 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: '0.8125rem',
        lineHeight: 1.6,
      },
      '&:hover > button': {
        opacity: 1,
      },
    },
    '& a': {
      color: theme.palette.secondary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.grey[300]}`,
      margin: '0.75em 0',
      paddingLeft: theme.spacing(1.5),
      color: theme.palette.text.secondary,
      '& p': {
        margin: '0.25em 0',
      },
    },
    '& table': {
      borderCollapse: 'collapse',
      fontSize: '0.8125rem',
      margin: '0.75em 0',
      width: '100%',
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.75, 1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 600,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(1.5, 0),
    },
    '& img': {
      maxWidth: '100%',
      borderRadius: 4,
    },
    '& span[title^="file-ref:"]': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontSize: '0.8125rem',
      color: theme.palette.secondary.main,
      backgroundColor: 'rgba(250, 70, 22, 0.06)',
      padding: '1px 6px',
      borderRadius: 4,
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      '&:hover': {
        backgroundColor: 'rgba(250, 70, 22, 0.14)',
        textDecoration: 'underline',
      },
    },
  },
}));

/**
 * Builds a set of character ranges in the markdown string that should NOT
 * be processed for file references (code blocks, inline code, links, HTML tags).
 */
function buildProtectedRanges(content: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];

  // Fenced code blocks: ```...```
  const fencedRe = /^(`{3,})[^\n]*\n[\s\S]*?^(\1)/gm;
  let m: RegExpExecArray | null;
  for (;;) {
    m = fencedRe.exec(content);
    if (m === null) {
      break;
    }
    ranges.push([m.index, m.index + m[0].length]);
  }

  // Inline code: `...`
  const inlineCodeRe = /`[^`\n]+`/g;
  for (;;) {
    m = inlineCodeRe.exec(content);
    if (m === null) {
      break;
    }
    ranges.push([m.index, m.index + m[0].length]);
  }

  // Link destinations: [text](url) and <url>
  const linkDestRe = /\]\([^)]*\)/g;
  for (;;) {
    m = linkDestRe.exec(content);
    if (m === null) {
      break;
    }
    ranges.push([m.index, m.index + m[0].length]);
  }

  // Existing HTML tags
  const htmlTagRe = /<[^>]+>/g;
  for (;;) {
    m = htmlTagRe.exec(content);
    if (m === null) {
      break;
    }
    ranges.push([m.index, m.index + m[0].length]);
  }

  // Sort by start position
  ranges.sort((a, b) => a[0] - b[0]);
  return ranges;
}

function isInProtectedRange(
  index: number,
  ranges: Array<[number, number]>,
): boolean {
  for (const [start, end] of ranges) {
    if (index >= start && index < end) return true;
    if (start > index) break;
  }
  return false;
}

/**
 * Pre-processes markdown content string to wrap file paths in <span> tags
 * with title="file-ref:..." attribute. This is a pure string transformation
 * that does NOT manipulate the DOM — safe to use during streaming.
 */
function preprocessFileRefs(content: string): string {
  if (!content) return content;

  const protected_ = buildProtectedRanges(content);
  let result = '';
  let lastIndex = 0;

  FILE_PATH_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  for (;;) {
    match = FILE_PATH_RE.exec(content);
    if (match === null) {
      break;
    }
    const prefix = match[1] ?? '';
    const displayPath = (match[2] ?? '') + match[3];
    const matchStart = match.index + prefix.length;

    // Skip if this match is inside a protected range
    if (isInProtectedRange(matchStart, protected_)) continue;

    // Append everything before this match (including the prefix delimiter)
    result += content.slice(lastIndex, matchStart);

    // Inject the span with title attribute (survives rehype-sanitize)
    const escaped = match[3]
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
    result += `<span title="file-ref:${escaped}">${displayPath
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')}</span>`;

    lastIndex = matchStart + displayPath.length;
  }

  // Append remainder
  if (lastIndex === 0) return content; // No matches found, return original
  result += content.slice(lastIndex);
  return result;
}

function createCopyButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.setAttribute(COPY_BUTTON_ATTR, '');
  btn.textContent = 'Copiar';
  btn.setAttribute('aria-label', 'Copiar código');
  Object.assign(btn.style, {
    position: 'absolute',
    top: '6px',
    right: '6px',
    opacity: '0',
    transition: 'opacity 0.2s ease',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '2px 8px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: '#555',
    lineHeight: '1.4',
  });
  return btn;
}

function handleCopyClick(e: Event) {
  const btn = e.currentTarget as HTMLButtonElement;
  const pre = btn.closest('pre');
  if (!pre) return;

  const code = pre.querySelector('code')?.textContent ?? pre.textContent ?? '';
  navigator.clipboard.writeText(code).then(() => {
    btn.textContent = 'Copiado!';
    setTimeout(() => {
      btn.textContent = 'Copiar';
    }, COPIED_TIMEOUT_MS);
  });
}

const MarkdownMessageInner = ({
  content,
  isStreaming,
}: MarkdownMessageProps) => {
  const classes = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);

  // Pre-process content string to inject file ref spans (pure string, no DOM)
  const processedContent = useMemo(
    () => preprocessFileRefs(content),
    [content],
  );

  // Copy buttons: only attach after streaming completes to avoid DOM conflicts
  useEffect(() => {
    if (isStreaming) {
      return undefined;
    }

    const root = rootRef.current;
    if (!root) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const preBlocks = root.querySelectorAll('pre');
      const buttons: HTMLButtonElement[] = [];

      preBlocks.forEach(pre => {
        if (pre.querySelector(`[${COPY_BUTTON_ATTR}]`)) return;
        const btn = createCopyButton();
        btn.addEventListener('click', handleCopyClick);
        pre.appendChild(btn);
        buttons.push(btn);
      });

      // Store for cleanup
      (root as any).__copyButtons = buttons;
    }, 100);

    return () => {
      clearTimeout(timer);
      const buttons: HTMLButtonElement[] = (root as any).__copyButtons ?? [];
      buttons.forEach(btn => {
        btn.removeEventListener('click', handleCopyClick);
        if (btn.parentNode) btn.remove();
      });
      (root as any).__copyButtons = [];
    };
  }, [processedContent, isStreaming]);

  // Event delegation for file ref clicks (React event, no DOM manipulation)
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN' && target.title?.startsWith('file-ref:')) {
      const path = target.title.slice('file-ref:'.length);
      navigator.clipboard.writeText(path);
      const original = target.textContent;
      target.textContent = 'Copiado!';
      setTimeout(() => {
        target.textContent = original;
      }, COPIED_TIMEOUT_MS);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }
      handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
    },
    [handleClick],
  );

  return (
    <div
      className={classes.root}
      ref={rootRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <MarkdownContent content={processedContent} dialect="gfm" />
    </div>
  );
};

export const MarkdownMessage = memo(MarkdownMessageInner);
